import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { type TaskStatusDto } from 'generated-sources/openapi';
import { HashRouter, Route, Routes } from 'react-router-dom';

import DunningActionsPage from 'components/DunningActionPage/DunningActionsPage';
import DunningForecastPage from 'components/DunningForecastPage/DunningForecastPage';
import { canAccessDunningForecast, FORECAST_PROPERTY_NAME } from 'components/DunningForecastPage/forecastAccess';
import DunningSelectionPage from 'components/DunningSelectionPage';
import ManualActionsPage from 'components/ManualActionsPage/ManualActionsPage';
import NavBar from 'components/NavBar/NavBar';
import RuleSetArchivePage from 'components/RuleSetArchivePage/RuleSetArchivePage';
import { Clerk, RIGHTS, type SystemProperty, type User } from 'interfaces/Interfaces';
import { getLicenseModel, getSystemProperties, getTaskStatuses } from 'services/ApiService';
import { getCurrentUser } from 'util/Util';

import ContractStatePage from './components/ContractStatePage/ContractStatePage';
import AdminPage from './components/Dev/AdminPage';
import DunningOverview from './components/DunningOverview/DunningOverview';
import SettingsPage from './components/SettingsPage/SettingsPage';
import { alertToast } from './components/Toast/AlertToast';
import { AppLicenseContext } from './license/AppLicense';

import './App.style.sass';

const user: User = getCurrentUser(new Clerk());

export const UserContext = React.createContext(user);
export const LicenseContext = React.createContext<AppLicenseContext>(new AppLicenseContext());
export type TaskStatusesState =
  | { status: 'loading'; tasks: undefined }
  | { status: 'ready'; tasks: TaskStatusDto[] }
  | { status: 'unavailable'; tasks: undefined };
export const TaskStatusesContext = React.createContext<TaskStatusesState>({ status: 'loading', tasks: undefined });
export const TASK_STATUS_REFRESH_INTERVAL = 60000;
export const ForecastContext = React.createContext<{
  forecastProperty?: SystemProperty;
  refreshForecastProperty: () => Promise<void>;
}>({
  forecastProperty: undefined,
  refreshForecastProperty: async () => undefined,
});

export function TaskStatusesProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [taskStatuses, setTaskStatuses] = useState<TaskStatusesState>({ status: 'loading', tasks: undefined });

  useEffect(() => {
    let mounted = true;
    function loadTaskStatuses() {
      getTaskStatuses()
        .then(({ tasks }) => {
          if (mounted) setTaskStatuses({ status: 'ready', tasks });
        })
        .catch(() => {
          if (!mounted) return;
          setTaskStatuses(current =>
            current.status === 'ready' ? current : { status: 'unavailable', tasks: undefined },
          );
        });
    }

    loadTaskStatuses();
    const intervalId = window.setInterval(loadTaskStatuses, TASK_STATUS_REFRESH_INTERVAL);
    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  return <TaskStatusesContext.Provider value={taskStatuses}>{children}</TaskStatusesContext.Provider>;
}

function App() {
  const [userRole, setUserRole] = useState(user);
  const [license, setLicense] = useState<AppLicenseContext>(new AppLicenseContext());
  const [forecastProperty, setForecastProperty] = useState<SystemProperty>();

  useEffect(() => {
    getLicenseModel().then(licenseModel => {
      setLicense(new AppLicenseContext(licenseModel, true));
    });
  }, []);

  const refreshForecastProperty = useCallback(
    () =>
      getSystemProperties()
        .then(properties => {
          const property = properties.find(({ property: name }) => name === FORECAST_PROPERTY_NAME);
          if (!property) {
            throw new Error(`System property ${FORECAST_PROPERTY_NAME} is missing`);
          }
          setForecastProperty(property);
        })
        .catch(alertToast),
    [],
  );

  useEffect(() => {
    refreshForecastProperty();
  }, [refreshForecastProperty]);

  const canAccessForecast = canAccessDunningForecast(userRole, forecastProperty);
  const forecastContext = useMemo(
    () => ({ forecastProperty, refreshForecastProperty }),
    [forecastProperty, refreshForecastProperty],
  );

  return (
    <UserContext.Provider value={userRole}>
      <LicenseContext.Provider value={license}>
        <TaskStatusesProvider>
          <ForecastContext.Provider value={forecastContext}>
            <div className="App">
              <HashRouter>
                <NavBar />
                <Routes>
                  <Route path="*" element={<DunningOverview />} />
                  <Route path="actions" element={<DunningActionsPage />} />
                  <Route path="contract-state" element={<ContractStatePage />} />

                  {userRole.rights.includes(RIGHTS.ACCESS_DUNNING_SELECTION) && (
                    <Route path="dunning-selection" element={<DunningSelectionPage />} />
                  )}
                  {canAccessForecast && <Route path="dunning-forecast" element={<DunningForecastPage />} />}
                  {userRole.rights.includes(RIGHTS.ACCESS_ADMIN_PAGE) && (
                    <Route
                      path="admin"
                      element={
                        <AdminPage
                          changeUserRole={setUserRole}
                          changeLicense={appLicense => setLicense(new AppLicenseContext(appLicense, license.loaded))}
                        />
                      }
                    />
                  )}
                  {userRole.rights.includes(RIGHTS.ACCESS_SETTINGS) && (
                    <Route path="settings" element={<SettingsPage />} />
                  )}
                  {userRole.rights.includes(RIGHTS.ACCESS_ARCHIVE) && (
                    <Route path="archive" element={<RuleSetArchivePage />} />
                  )}
                  {userRole.rights.includes(RIGHTS.ACCESS_MANUAL_ACTIONS) && (
                    <Route path="manual-actions" element={<ManualActionsPage />} />
                  )}
                </Routes>
              </HashRouter>
            </div>
          </ForecastContext.Provider>
        </TaskStatusesProvider>
      </LicenseContext.Provider>
    </UserContext.Provider>
  );
}

export default App;
